import { ptBR } from 'date-fns/locale'
import dayjs from 'dayjs'
import { CalendarIcon, CheckIcon, ChevronDownIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { DateRange } from 'react-day-picker'

import {
  Badge,
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@/_components/ui'
import { cn } from '@/_lib/utils'
import { useSearchParams } from '@/features/_hooks/use-search-params'

import { formatDate } from '../_helpers/date'

const PRESETS = [
  { label: 'Hoje', value: 'today', range: () => ({ from: dayjs(), to: dayjs() }) },
  {
    label: 'Ontem',
    value: 'yesterday',
    range: () => ({ from: dayjs().subtract(1, 'day'), to: dayjs().subtract(1, 'day') }),
  },
  {
    label: 'Últimos 7 dias',
    value: '7d',
    range: () => ({ from: dayjs().subtract(7, 'days'), to: dayjs() }),
  },
  {
    label: 'Últimos 30 dias',
    value: '30d',
    range: () => ({ from: dayjs().subtract(30, 'days'), to: dayjs() }),
  },
  {
    label: 'Últimos 3 meses',
    value: '3mo',
    range: () => ({ from: dayjs().subtract(3, 'months'), to: dayjs() }),
  },
  {
    label: 'Últimos 6 meses',
    value: '6mo',
    range: () => ({ from: dayjs().subtract(6, 'months'), to: dayjs() }),
  },
  {
    label: 'Último ano',
    value: '1y',
    range: () => ({ from: dayjs().subtract(1, 'year'), to: dayjs() }),
  },
] as const

function formatAsISODate(d: dayjs.Dayjs) {
  return d.format('YYYY-MM-DD')
}

function findMatchingPreset(from: string, to: string): string | null {
  for (const preset of PRESETS) {
    const range = preset.range()
    if (formatAsISODate(range.from) === from && formatAsISODate(range.to) === to) {
      return preset.value
    }
  }
  return null
}

export function DateRangeFilter({ disabled }: { disabled?: boolean } = {}) {
  const { searchParams, setSearchParams } = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)

  const startDateParam = (searchParams as Record<string, unknown>)?.startDate as string | undefined
  const endDateParam = (searchParams as Record<string, unknown>)?.endDate as string | undefined

  const setDateParams = (startDate: string | undefined, endDate: string | undefined) => {
    setSearchParams({ startDate, endDate })
  }

  // Derive display state from URL
  const appliedRange = useMemo<DateRange | undefined>(() => {
    if (startDateParam && endDateParam) {
      return { from: new Date(startDateParam), to: new Date(endDateParam) }
    }
    return undefined
  }, [startDateParam, endDateParam])

  const appliedPreset = useMemo<string | null>(() => {
    if (startDateParam && endDateParam) return findMatchingPreset(startDateParam, endDateParam)
    return null
  }, [startDateParam, endDateParam])

  // Local state for in-progress calendar selection (incomplete range)
  const [calendarRange, setCalendarRange] = useState<DateRange | undefined>(undefined)

  const applyRange = (from: dayjs.Dayjs, to: dayjs.Dayjs) => {
    setDateParams(formatAsISODate(from), formatAsISODate(to))
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      setCalendarRange(appliedRange)
    }
  }

  const handlePresetClick = (preset: (typeof PRESETS)[number]) => {
    const { from, to } = preset.range()
    setCalendarRange({ from: from.toDate(), to: to.toDate() })
    applyRange(from, to)
  }

  const handleCalendarSelect = (range: DateRange | undefined) => {
    setCalendarRange(range)
    if (range?.from && range?.to) {
      applyRange(dayjs(range.from), dayjs(range.to))
    } else if (!range?.from) {
      setDateParams(undefined, undefined)
    }
  }

  const handleClear = () => {
    setCalendarRange(undefined)
    setDateParams(undefined, undefined)
    setIsOpen(false)
  }

  const displayLabel = useMemo(() => {
    if (appliedPreset) {
      return PRESETS.find((p) => p.value === appliedPreset)?.label ?? null
    }
    if (appliedRange?.from && appliedRange?.to) {
      return `${formatDate(appliedRange.from)} - ${formatDate(appliedRange.to)}`
    }
    return null
  }, [appliedPreset, appliedRange])

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 font-medium" disabled={disabled}>
          <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
          Período
          {displayLabel && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge className="rounded-sm bg-transparent px-1 font-bold text-brand-green-primary-5 hover:bg-transparent">
                {displayLabel}
              </Badge>
            </>
          )}
          <ChevronDownIcon className="ml-1 h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="flex flex-col gap-1 border-r p-3">
            <p className="mb-1 px-2 text-xs font-medium text-muted-foreground">Atalhos</p>
            {PRESETS.map((preset) => (
              <button
                key={preset.value}
                onClick={() => handlePresetClick(preset)}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent',
                  appliedPreset === preset.value && 'bg-accent font-medium',
                )}
              >
                <CheckIcon
                  className={cn(
                    'h-3.5 w-3.5 shrink-0',
                    appliedPreset === preset.value ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {preset.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col p-2">
            <Calendar
              mode="range"
              selected={calendarRange}
              onSelect={handleCalendarSelect}
              numberOfMonths={2}
              defaultMonth={dayjs().subtract(1, 'month').toDate()}
              disabled={{ after: new Date() }}
              locale={ptBR}
            />
            <button
              onClick={handleClear}
              className="mb-3 mr-3 self-end text-xs text-muted-foreground hover:text-foreground"
            >
              Limpar filtros
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
